import { z } from 'zod';

import {
  DISTRIBUTION_TYPES,
  GEO_LEVELS,
  POD_OPTIMIZATION_MODES,
  SALES_CHANNELS,
} from 'watchtower-ui/pages/OptimizationPages/podOptimizer/podOptimizerTypes';
import type { PodOptimizationResultRow } from 'watchtower-ui/pages/OptimizationPages/podOptimizer/podOptimizerTypes';
import type { PodOptimizerFormState } from 'watchtower-ui/redux/slices/podOptimizer';

export const POD_OPTIMIZER_FORM_STORAGE_KEY = 'pod_optimizer_form_v1';

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const podRuleChannelFilterSchema = z.object({
  id: z.string(),
  enabled: z.boolean(),
  salesChannel: optionSchema,
  regions: z.array(optionSchema),
  states: z.array(optionSchema),
  rootgeos: z.array(optionSchema),
  brands: z.array(optionSchema),
  minPodChange: z.string(),
  maxPodChange: z.string(),
});

const podRuleSchema = z.object({
  id: z.string(),
  distributionType: optionSchema,
  regions: z.array(optionSchema),
  states: z.array(optionSchema),
  rootgeos: z.array(optionSchema),
  brands: z.array(optionSchema),
  minPodChange: z.string(),
  maxPodChange: z.string(),
  channelFilters: z.array(podRuleChannelFilterSchema),
});

const podOptimizationResultRowSchema: z.ZodType<PodOptimizationResultRow> = z.lazy(() =>
  z.object({
    id: z.string(),
    brand: z.string(),
    salesChannel: z.enum(SALES_CHANNELS),
    distributionType: z.enum(DISTRIBUTION_TYPES),
    week: z.string(),
    geoLevel: z.enum(GEO_LEVELS),
    geo: z.string(),
    national: z.string(),
    region: z.string(),
    state: z.string(),
    rootgeo: z.string(),
    baselinePods: z.number(),
    recommendedPodChange: z.number(),
    optimizedPods: z.number(),
    expectedVolumeImpact: z.number(),
    totalIncrementalVolume: z.number(),
    totalPodChange: z.number(),
    optimizationMode: z.enum(POD_OPTIMIZATION_MODES),
    filterValuesUsed: z.string(),
    incrementalPodImpact: z.number(),
    lastPodImpact: z.number(),
    maxPossiblePods: z.number(),
    incrementalVolume: z.number(),
    incrementalVolumePerChangedPod: z.number(),
    maxPodChange: z.number(),
    timePeriod: z.string().optional(),
    children: z.array(podOptimizationResultRowSchema).optional(),
  }),
);

const podOptimizationResultSchema = z.object({
  rows: z.array(podOptimizationResultRowSchema),
  mode: z.enum(POD_OPTIMIZATION_MODES),
  weeklyTargetPods: z.number(),
  weekCount: z.number(),
  totalPodChange: z.number(),
  totalIncrementalVolume: z.number(),
  filterValuesUsed: z.string(),
  incrementalVolumePerChangedPod: z.number(),
  lastPodImpact: z.number(),
});

const podOptimizerFormStateSchema: z.ZodType<PodOptimizerFormState> = z.object({
  selectedBrandOverride: z.array(optionSchema).nullable(),
  periodRangeTimestamps: z.tuple([z.number(), z.number()]).nullable(),
  mode: z.enum(POD_OPTIMIZATION_MODES),
  targetPods: z.string(),
  ruleOverride: z.array(podRuleSchema).nullable(),
  uploadedCsvData: z.array(z.record(z.string(), z.unknown())).nullable(),
  result: podOptimizationResultSchema.nullable(),
  searchTerm: z.string(),
  csvValidationMessages: z.array(z.string()),
});

const persistedPodOptimizerFormSchema = z.object({
  version: z.literal(1),
  state: podOptimizerFormStateSchema,
});

type PodOptimizerStorage = Pick<Storage, 'getItem' | 'setItem'>;

const getSessionStorage = (): PodOptimizerStorage | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

export const loadPodOptimizerFormState = (
  storage: PodOptimizerStorage | undefined = getSessionStorage(),
): PodOptimizerFormState | undefined => {
  if (!storage) {
    return undefined;
  }

  try {
    const serializedState = storage.getItem(POD_OPTIMIZER_FORM_STORAGE_KEY);
    if (!serializedState) {
      return undefined;
    }

    const parsedState: unknown = JSON.parse(serializedState);
    const result = persistedPodOptimizerFormSchema.safeParse(parsedState);

    return result.success ? result.data.state : undefined;
  } catch {
    return undefined;
  }
};

export const persistPodOptimizerFormState = (
  state: PodOptimizerFormState,
  storage: PodOptimizerStorage | undefined = getSessionStorage(),
): boolean => {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(POD_OPTIMIZER_FORM_STORAGE_KEY, JSON.stringify({ version: 1, state }));
    return true;
  } catch {
    return false;
  }
};
